import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useData } from '../context/DataContext';

const TablaLogistica = () => {
  const [openInfo, setOpenInfo] = useState(false);
  const { getThreeCosts, dataReady } = useData();

  // Get data from Excel
  const excelData = dataReady ? getThreeCosts() : {
    labels: [],
    transport2022: [], distribution2022: [], logistics2022: [],
    transport2023: [], distribution2023: [], logistics2023: [],
    transport2024: [], distribution2024: [], logistics2024: []
  };

  // Build table data from Excel data
  const buildTableData = (transportData, distributionData, logisticsData) => {
    return excelData.labels.map((label, idx) => {
      const transport = transportData[idx] || 0;
      const distribution = distributionData[idx] || 0;
      const logistics = logisticsData[idx] || 0;
      return [
        label,
        `${transport}%`,
        `${distribution}%`,
        `${logistics}%`,
      ];
    });
  };

  const data2022 = excelData.labels.length > 0
    ? buildTableData(excelData.transport2022, excelData.distribution2022, excelData.logistics2022)
    : [
        ['Servicios logísticos', '19%', '22%', '24%'],
        ['Automotriz', '7%', '10%', '11%'],
        ['Manufactura', '4%', '6%', '9%'],
        ['Alimentos', '5%', '6%', '9%'],
        ['Construcción', '5%', '6%', '7%'],
        ['Sector salud/Farmacéuticas', '4%', '5%', '8%'],
        ['Retail', '3%', '4%', '6%'],
      ];

  const data2023 = excelData.labels.length > 0
    ? buildTableData(excelData.transport2023, excelData.distribution2023, excelData.logistics2023)
    : [
        ['Servicios logísticos', '13%', '15%', '17%'],
        ['Automotriz', '2%', '14%', '15%'],
        ['Manufactura', '3%', '4%', '5%'],
        ['Alimentos', '9%', '15%', '13%'],
        ['Construcción', '11%', '11%', '11%'],
        ['Sector salud/Farmacéuticas', '8%', '3%', '12%'],
        ['Retail', '4%', '15%', '11%'],
      ];

  const data2024 = excelData.labels.length > 0
    ? buildTableData(excelData.transport2024, excelData.distribution2024, excelData.logistics2024)
    : [
        ['Servicios logísticos', '6%', '10%', '12%'],
        ['Automotriz', '6%', '9%', '13%'],
        ['Manufactura', '7%', '9%', '13%'],
        ['Alimentos', '10%', '18%', '27%'],
        ['Construcción', '11%', '19%', '31%'],
        ['Sector salud/Farmacéuticas', '3%', '5%', '8%'],
        ['Retail', '7%', '12%', '14%'],
      ];

  const columnas = ['Transporte', 'Distribución', 'Logístico Total'];

  const infoContent = {
    analisis: `El costo logístico total como proporción de las ventas en tres periodos consecutivos, desglosado en transporte, distribución y total logístico por industria. Se observan cambios importantes en la estructura de costos logísticos entre 2023, 2024 y 2025.`,
    formula: `Promedio de los costos como % de las ventas totales`,
    descripcion: `Los datos evidencian que el costo logístico total sigue siendo un reto relevante para muchas industrias, especialmente en aquellas con mayores exigencias de cobertura y servicio al cliente.`,
  };

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MuiTooltip title="Información de la tabla">
          <IconButton onClick={() => setOpenInfo(true)} size="small">
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>

      <Typography variant="h6" align="center" gutterBottom>
        Costo Logístico como % de las Ventas Totales (2023)
      </Typography>
      <Tabla datos={data2022} columnas={columnas} año="2023" />

      <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
        Costo Logístico como % de las Ventas Totales (2024)
      </Typography>
      <Tabla datos={data2023} columnas={columnas} año="2024" />

      <Typography variant="h6" align="center" gutterBottom sx={{ mt: 4 }}>
        Costo Logístico como % de las Ventas Totales (2025)
      </Typography>
      <Tabla datos={data2024} columnas={columnas} año="2025" />

      <Dialog open={openInfo} onClose={() => setOpenInfo(false)}>
        <DialogTitle>Información de las Tablas</DialogTitle>
        <DialogContent>
          <Typography paragraph><strong>Análisis:</strong> {infoContent.analisis}</Typography>
          <Typography paragraph><strong>Fórmula:</strong> {infoContent.formula}</Typography>
          <Typography paragraph><strong>Descripción:</strong> {infoContent.descripcion}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInfo(false)} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Tabla = ({ datos, columnas, año }) => {
  // Color scheme matching the app
  const getYearColor = (year) => {
    switch(year) {
      case '2023': return '#A2AEBB';
      case '2024': return '#476A8E';
      case '2025': return '#1F3D58';
      default: return '#cce5ff';
    }
  };

  return (
    <Box sx={{ overflowX: 'auto', mb: 4 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, backgroundColor: '#e6efdb' }}>Industria</th>
            {columnas.map((label, i) => (
              <th key={i} style={{ ...thStyle, backgroundColor: '#e6efdb' }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {datos.map((fila, idx) => (
            <tr key={idx}>
              <td style={{ ...tdStyle, fontWeight: 'bold' }}>{fila[0]}</td>
              {fila.slice(1).map((valor, i) => (
                <td
                  key={i}
                  style={{
                    ...tdStyle,
                    backgroundColor: getYearColor(año),
                    color: año === '2025' || año === '2024' ? '#fff' : '#000'
                  }}
                >
                  {valor}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

const thStyle = {
  border: '1px solid #ccc',
  padding: '8px',
  textAlign: 'center',
  fontWeight: 'bold',
};

const tdStyle = {
  border: '1px solid #ccc',
  padding: '8px',
  textAlign: 'center',
};

export default TablaLogistica;
